import { Injectable } from '@angular/core';
import { AsyncSubject, BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap } from 'rxjs/operators';

import { AuthPayload, AuthState, AuthUser } from '../models/auth.model';
import { AuthGateway } from '../usecases/auth.gateway';
import { AuthUsecase } from '../usecases/auth.usecase';
import { DatadogGateway } from '../usecases/datadog.gateway';
import { ProgressUsecase } from '../usecases/progress.usecase';

@Injectable()
export class AuthInteractor extends AuthUsecase {
  get authState$(): Observable<AuthState> {
    return this._authState.pipe(filter(({ status }) => status !== 'none'));
  }
  get token$(): Observable<string> {
    return this._authGateway.fetchAuthSession().pipe(map(session => session.tokens?.idToken?.toString() ?? ''));
  }
  get payload$(): Observable<AuthPayload> {
    return this._authGateway.fetchAuthSession().pipe(map(session => session.tokens?.idToken?.payload as AuthPayload));
  }

  private readonly _authState = new BehaviorSubject<AuthState>({ status: 'none' });

  constructor(
    private _progressUsecase: ProgressUsecase,
    private _authGateway: AuthGateway,
    private _datadogGateway: DatadogGateway,
  ) {
    super();
    this._authGateway
      .fetchAuthSession()
      .pipe(
        catchError(() => of(undefined)),
        mergeMap(session => {
          if (session?.tokens?.idToken) {
            return this.fetchAuthUser().pipe(
              catchError(() => of(undefined)),
              map(user => ({ status: user ? 'signedIn' : 'signIn', user }) as AuthState),
            );
          } else {
            return of({ status: 'signIn' } as AuthState);
          }
        }),
      )
      .subscribe(state => this.setAuthState(state));

    this._authState
      .pipe(
        filter(({ status }) => status === 'signedIn'),
        mergeMap(({ user }) => this.payload$.pipe(map(payload => ({ user, payload })))),
      )
      .subscribe(({ user, payload }) => {
        this._datadogGateway.setUser(user?.attributes?.name, payload.organizationId);
      });
  }

  signIn(): Observable<never> {
    const progressId = this._progressUsecase.show();
    const result = new AsyncSubject<never>();
    this._authGateway
      .signInWithRedirect()
      .pipe(finalize(() => this._progressUsecase.dismiss(progressId)))
      .subscribe({
        error: result.error.bind(result),
        complete: result.complete.bind(result),
      });
    return result.asObservable();
  }

  setAuthState(state: AuthState): void {
    this._authState.next(state);
  }

  private fetchAuthUser(): Observable<AuthUser> {
    return this._authGateway.fetchUserAttributes().pipe(
      map(attributes => ({
        attributes: {
          name: attributes?.name ?? '',
        },
      })),
    );
  }
}
